import type { CampaignCreationCapabilities } from "@adpilot/shared";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useRef } from "react";
import { AdGroupSection } from "@/components/campaign-creation/wizard/AdGroupSection";
import { CampaignSettingsSection } from "@/components/campaign-creation/wizard/CampaignSettingsSection";
import type { CampaignCreationForm } from "@/components/campaign-creation/wizard/ConfigurationFields";
import type { SectionId } from "@/components/campaign-creation/wizard/wizard-navigation";
import type { CampaignCreationInvalidProduct } from "@/lib/campaign-creation-session";
import { fetchPortfolios } from "@/lib/portfolio-api";

/** Step 2 of the Sponsored Products wizard: campaign settings plus the ad group. */
export function ConfigurationStep({
  capabilities,
  form,
  selectedShopId,
  initialPortfolioId,
  activeSection,
  enterInput,
  onEnterInputChange,
  negativeKeywordInput,
  onNegativeKeywordInputChange,
  negativeProductInput,
  onNegativeProductInputChange,
  invalidProducts,
  onInvalidProductsChange,
  onCatalogBlockingChange,
  onReconnect,
}: {
  capabilities: CampaignCreationCapabilities;
  form: CampaignCreationForm;
  selectedShopId: string;
  /** ADP-137: preselect this portfolio when it exists in the shop's list. */
  initialPortfolioId?: string | null;
  activeSection: SectionId;
  enterInput: string;
  onEnterInputChange: (input: string) => void;
  negativeKeywordInput: string;
  onNegativeKeywordInputChange: (input: string) => void;
  negativeProductInput: string;
  onNegativeProductInputChange: (input: string) => void;
  invalidProducts: CampaignCreationInvalidProduct[];
  onInvalidProductsChange: (products: CampaignCreationInvalidProduct[]) => void;
  onCatalogBlockingChange: (blocked: boolean) => void;
  onReconnect: () => void;
}) {
  const values = form.watch();
  // ADP-137: real portfolio select (was a disabled placeholder). Same source as
  // the SD form; the ambient shop context matches selectedShopId here.
  const portfolios = useQuery({
    queryKey: ["portfolios", "campaign-creation", selectedShopId],
    queryFn: () => fetchPortfolios(),
  });
  const selectablePortfolios = (portfolios.data ?? []).filter(
    (portfolio) => portfolio.state !== "archived",
  );
  const portfolioPrefillApplied = useRef(false);
  useEffect(() => {
    if (portfolioPrefillApplied.current || !initialPortfolioId) return;
    // Membership in the shop-scoped, non-archived list IS the shop check; a
    // foreign or archived id simply never matches and the field stays empty.
    if (!selectablePortfolios.some((portfolio) => portfolio.id === initialPortfolioId)) return;
    portfolioPrefillApplied.current = true;
    if (form.getValues("portfolioId") == null) {
      form.setValue("portfolioId", initialPortfolioId);
    }
  }, [initialPortfolioId, selectablePortfolios, form]);
  const currencySymbol = capabilities.currencyCode === "USD" ? "$" : capabilities.currencyCode;
  return (
    <div className="flex min-w-0 flex-col gap-4">
      <CampaignSettingsSection
        capabilities={capabilities}
        form={form}
        values={values}
        activeSection={activeSection}
        selectablePortfolios={selectablePortfolios}
        currencySymbol={currencySymbol}
      />
      <AdGroupSection
        capabilities={capabilities}
        form={form}
        values={values}
        selectedShopId={selectedShopId}
        activeSection={activeSection}
        currencySymbol={currencySymbol}
        enterInput={enterInput}
        onEnterInputChange={onEnterInputChange}
        negativeKeywordInput={negativeKeywordInput}
        onNegativeKeywordInputChange={onNegativeKeywordInputChange}
        negativeProductInput={negativeProductInput}
        onNegativeProductInputChange={onNegativeProductInputChange}
        invalidProducts={invalidProducts}
        onInvalidProductsChange={onInvalidProductsChange}
        onCatalogBlockingChange={onCatalogBlockingChange}
        onReconnect={onReconnect}
      />
    </div>
  );
}
